import styled from 'styled-components'
import { useFeatureFlags, useUpdateFeatureFlagOptIns } from '../../services/api/feature-flags.hooks'
import { Border, Colors, Spacing, Typography } from '../../styles'
import { icons } from '../../styles/images'
import Flex from '../atoms/Flex'
import GTSwitch from '../atoms/GTSwitch'
import { Icon } from '../atoms/Icon'
import Spinner from '../atoms/Spinner'
import { BodyLarge, BodyMedium, BodySmall } from '../atoms/typography/Typography'

const Banner = styled.div`
    display: flex;
    gap: ${Spacing._12};
    align-items: flex-start;
    padding: ${Spacing._16};
    border: ${Border.stroke.large} solid ${Colors.accent.yellowStroke};
    border-radius: ${Border.radius.medium};
    background-color: ${Colors.accent.yellow};
    color: ${Colors.text.ink};
`
const BannerTitle = styled.div`
    ${Typography.title.large};
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`
const ExperimentalBadge = styled.span`
    ${Typography.label.small};
    display: inline-flex;
    padding: ${Spacing._2} ${Spacing._8};
    border: ${Border.stroke.medium} solid ${Colors.accent.yellowStroke};
    border-radius: ${Border.radius.medium};
    background-color: ${Colors.accent.yellow};
    color: ${Colors.text.ink};
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`
const FlagRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing._16};
    padding: ${Spacing._12};
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    border-radius: ${Border.radius.medium};
`

// Lists every feature that is off for everyone, so any user can opt in ahead of the global rollout.
// Features already on for everyone are not listed: there is nothing to opt in to.
const ExperimentalFeatureSettings = () => {
    const { data: featureFlags, isLoading } = useFeatureFlags()
    const { mutate: updateOptIns, isLoading: isUpdating } = useUpdateFeatureFlagOptIns()
    if (isLoading) return <Spinner />
    const experimentalFlags = featureFlags?.flags?.filter((flag) => !flag.globally_enabled) ?? []

    return (
        <Flex column gap={Spacing._24} data-testid="experimental-features">
            <Banner role="note">
                <Icon icon={icons.flask} color="black" />
                <Flex column gap={Spacing._4}>
                    <BannerTitle>Experimental</BannerTitle>
                    <BodyMedium color="ink">
                        These features may not be fully tested or ready yet. They could change, break, or go away.
                    </BodyMedium>
                </Flex>
            </Banner>
            {experimentalFlags.length === 0 ? (
                <BodyMedium color="light">No experimental features right now.</BodyMedium>
            ) : (
                experimentalFlags.map((flag) => {
                    // Internal dashboard access turns every flag on; there is nothing for the switch to change.
                    const isForcedOn = flag.enabled && !flag.opted_in
                    return (
                        <FlagRow key={flag.key}>
                            <Flex column gap={Spacing._4}>
                                <Flex alignItems="center" gap={Spacing._8}>
                                    <BodyLarge>{flag.name}</BodyLarge>
                                    <ExperimentalBadge>Experimental</ExperimentalBadge>
                                </Flex>
                                <BodySmall color="light">
                                    {isForcedOn ? 'Always on for internal access' : flag.description}
                                </BodySmall>
                            </Flex>
                            <GTSwitch
                                label={flag.name}
                                checked={flag.enabled}
                                testId={`${flag.key}-experimental-toggle`}
                                disabled={isUpdating || isForcedOn}
                                onChange={(enabled) => updateOptIns({ [flag.key]: enabled })}
                            />
                        </FlagRow>
                    )
                })
            )}
        </Flex>
    )
}

export default ExperimentalFeatureSettings
